import React from 'react';
import { formatCurrency } from 'utils';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Chip, Stack, Typography } from '@mui/material';

import { roundToCents, calculateNetTrade } from '../../../../lib';
import { DealBuilderFormValues } from '../../../../types';

interface Props {
  form: UseFormReturn<DealBuilderFormValues>;
}

const Total = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack direction="row" gap="0.5rem" alignItems="center">
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="subtitle2">{value}</Typography>
  </Stack>
);

export const TradeInTotals = ({ form }: Props) => {
  const tradeIns = useWatch({ control: form.control, name: 'pricing.tradeIn' }) ?? [];

  const totals = tradeIns.reduce(
    (acc, row) => ({
      tradeInValue: acc.tradeInValue + (row?.tradeInValue ?? 0),
      overUnder: acc.overUnder + (row?.overUnderAllowance ?? 0),
      payOff: acc.payOff + (row?.amountOwed ?? 0),
      netTrade: acc.netTrade + calculateNetTrade({ tradeInValue: row?.tradeInValue, amountOwed: row?.amountOwed }),
    }),
    { tradeInValue: 0, overUnder: 0, payOff: 0, netTrade: 0 }
  );

  const overUnder = roundToCents(totals.overUnder);

  return (
    <Stack direction="row" gap="1.5rem" alignItems="center" justifyContent="flex-end" flexWrap="wrap">
      <Total label="Total Trade-in Value" value={formatCurrency(roundToCents(totals.tradeInValue), true)} />

      <Total
        label="Total Over/Under"
        value={
          <Stack direction="row" gap="0.5rem" alignItems="center">
            <Typography variant="subtitle2" color={overUnder > 0 ? 'warning.main' : 'inherit'}>
              {formatCurrency(Math.abs(overUnder), true)}
            </Typography>

            {overUnder !== 0 && (
              <Chip
                size="small"
                color={overUnder > 0 ? 'warning' : 'info'}
                label={
                  <Typography variant="caption" sx={{ textTransform: 'none' }}>
                    {overUnder > 0 ? 'Over' : 'Under'}
                  </Typography>
                }
              />
            )}
          </Stack>
        }
      />

      <Total label="Total Trade Pay-Off" value={formatCurrency(roundToCents(totals.payOff), true)} />

      <Total
        label="Total Net Trade"
        value={
          <Typography variant="subtitle2" color="success.main">
            {formatCurrency(roundToCents(totals.netTrade), true)}
          </Typography>
        }
      />
    </Stack>
  );
};
